/**
 * `SelectionSets_Executor.ts` — port of Unity's
 * `Executor/SelectionSets_Executor.cs`.
 */

import type { IProjectModel } from "@models/project/IProjectModel";
import type { EntitySnapshot } from "@models/data/entityData";
import { buildSceneMergedAncestorIndex } from "@models/data/entityData";
import type { SelectionSetData } from "@models/data/selectionSetData";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import type { AgentToolExecutorHost } from "./AgentToolExecutorModel";
import {
  buildPathIndex,
  displayNameOf,
  normalizePath,
  resolveManyByPath,
} from "@ai/context/hierarchyPaths";
import {
  getString,
  getStringList,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

export class SelectionSetsExecutor implements IDomainExecutor {
  private readonly m_project: IProjectModel;
  private readonly m_host: AgentToolExecutorHost;

  constructor(project: IProjectModel, host: AgentToolExecutorHost) {
    this.m_project = project;
    this.m_host = host;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.CreateSelectionSet:
        return this.createSelectionSet(args);
      case AgentToolNames.RemoveSelectionSet:
        return this.removeSelectionSet(args);
      case AgentToolNames.CountParts:
        return this.countParts(args);
      default:
        return null;
    }
  }

  private async createSelectionSet(args: ToolArgs): Promise<string> {
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return "error: no active scene";

    const paths = getStringList(args, "partPaths");
    if (paths.length === 0) return "error: create_selection_set_missing_partPaths";
    const entries = resolveManyByPath(this.m_project.getEntities(sceneId), paths);
    if (entries.length === 0) {
      return "error: no parts found at the given path(s). Resolve targets with Get_Scene_Context first.";
    }

    const setName = getString(args, "setName") ?? undefined;
    const created = await this.m_project.createSelectionSetAsync(
      sceneId,
      entries.map((e) => e.snapshot.id),
      setName,
    );
    if (!created) return "error: create_selection_set_failed";
    return `ok: created selection set '${created.Name}' with ${entries.length} part(s)`;
  }

  private async removeSelectionSet(args: ToolArgs): Promise<string> {
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return "error: no active scene";
    const setName = getString(args, "setName");
    if (!setName) return "error: remove_selection_set_missing_setName";

    const set = this.m_project
      .getSelectionSets(sceneId)
      .find((s) => s.Name.toLowerCase() === setName.toLowerCase());
    if (!set) return `error: selection set '${setName}' not found`;

    await this.m_project.deleteSelectionSetAsync(sceneId, set.ID);
    return `ok: removed selection set '${set.Name}'`;
  }

  /**
   * Counting is answered from selection sets rather than from the hierarchy at
   * large, because a count the agent tallies off a digest is a guess that
   * sounds like a fact. The author decides what is countable by grouping it;
   * everything else honestly resolves to "no selection set", which is the
   * signal to look in the documents or say so.
   *
   * A set is found by what it CONTAINS, and only by its NAME when nothing
   * contains the part. Authors name sets "Set 2" as often as "Top Cover
   * Screws": matching the name alone made the first case uncountable, and
   * matching members alone made the second one uncountable when its parts are
   * called "Mesh_001". Members are tried first, so the name can never take a
   * set the contents already answered for.
   *
   * Every non-count outcome returns as JSON rather than `error:` — "this part
   * is in no selection set" is a real answer to give the user, not a failed
   * call.
   */
  private async countParts(args: ToolArgs): Promise<string> {
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return "error: no active scene";

    // `setName` is the argument’s pre-2026-08-25 name, still accepted because a
    // model that saw the old description passes it. It always carried the KIND
    // of part the user asked about, never a set’s name, so it feeds the same
    // member search.
    const partName = getString(args, "partName") || getString(args, "setName");
    const partPath = getString(args, "partPath");
    if (!partName && !partPath) {
      return "error: Count_Parts_missing_target. Pass partName for a kind of part, or partPath for the part the user is pointing at.";
    }

    const sets = this.m_project.getSelectionSets(sceneId);
    // No sets at all is its own answer, and a different one from "the part is
    // in none of them": nothing in this scene can be counted, so the caller
    // goes straight to the documents.
    if (sets.length === 0) {
      return JSON.stringify({ count: null, reason: "no_selection_sets_in_scene" });
    }

    const entities = this.m_project.getEntities(sceneId);
    const index = buildPathIndex(entities);
    const byId = new Map(index.map((e) => [e.snapshot.id, e.snapshot]));

    let matches: ReadonlyArray<SelectionSetData>;
    // Which signal found the set. Reported so the caller can tell a count of
    // parts it recognised from a count of whatever a matching set happened to
    // hold.
    let matchedBy: "members" | "set name" | "pointed part" = "members";
    if (partPath) {
      matchedBy = "pointed part";
      const entry = index.find((e) => e.path === normalizePath(partPath));
      if (!entry) return `error: part not found at path '${partPath}'`;
      // A part folded into a merged node is not addressable on its own, so the
      // set holds the merged node — the same redirect picking applies before
      // the pointed-at id ever reaches the agent.
      const candidateIds = new Set([entry.snapshot.id]);
      const mergedAncestor = buildSceneMergedAncestorIndex(entities).get(entry.snapshot.id);
      if (mergedAncestor) candidateIds.add(mergedAncestor);

      matches = sets.filter((s) =>
        (s.EntitySnapshotIDs ?? []).some((id) => candidateIds.has(id)),
      );
      if (matches.length === 0) {
        return JSON.stringify({
          count: null,
          reason: "part_not_in_any_selection_set",
          part: displayNameOf(entry.snapshot),
          path: entry.path,
        });
      }
    } else {
      // Search what the sets CONTAIN first. Authors name them "Set 2" as
      // readily as "Top Cover Screws", and a lookup that reads only the name
      // finds nothing in the first case however the user phrases the question.
      const tokens = searchTokens(partName!);
      let found = sets.filter((set) => coversEveryToken(tokens, memberTexts(set, byId)));

      // Only when NOTHING contains it does the name get a say — the mirror
      // case, a set called "Top Cover Screws" whose members are "Mesh_001".
      // Members win outright whenever they match, so this fallback cannot
      // bring back the name-only lookup it replaced.
      if (found.length === 0) {
        found = sets.filter((set) => coversEveryToken(tokens, [set.Name]));
        matchedBy = "set name";
      }

      matches = found;
      if (matches.length === 0) {
        return JSON.stringify({
          count: null,
          reason: "part_not_in_any_selection_set",
          requested: partName,
          selectionSetsSearched: sets.length,
        });
      }
    }

    if (matches.length > 1) {
      return JSON.stringify({
        count: null,
        reason: "several_selection_sets_match",
        matchedBy,
        // Members, not just names: a set called "Set 2" tells the user nothing,
        // so the caller needs contents to offer a choice worth making.
        candidates: matches.map((set) => describeSet(set, byId)),
      });
    }

    const set = matches[0];
    let count = 0;
    let mergedNodesExpanded = 0;
    let membersMissingFromScene = 0;
    for (const id of set.EntitySnapshotIDs ?? []) {
      const snapshot = byId.get(id);
      if (!snapshot) {
        membersMissingFromScene++;
        continue;
      }
      // Merging is a rendering decision, not a statement about how many parts
      // there are: for counting only, a merged member is worth the parts folded
      // into it.
      const leaves = snapshot.hasMergedChildren ? countLeafDescendants(snapshot) : 0;
      if (leaves > 0) {
        count += leaves;
        mergedNodesExpanded++;
      } else {
        count += 1;
      }
    }

    // The source line the Answer skill attributes a count with. Published to
    // the panel rather than left for the model to write: the realtime session
    // emits audio only, so a line in the reply is a line Spark reads aloud —
    // and "Source: Set 2" is a label to glance at, not a sentence to hear. The
    // set is named verbatim; it is the author's text, not ours to re-case.
    this.m_host.conversationPresentation?.publishAttribution(`Source: ${set.Name}`);

    return JSON.stringify({
      selectionSet: set.Name,
      count,
      source: "selection set",
      matchedBy,
      ...(mergedNodesExpanded > 0 ? { mergedNodesExpanded } : {}),
      ...(membersMissingFromScene > 0 ? { membersMissingFromScene } : {}),
    });
  }
}

/**
 * Words worth matching on, from either a user’s phrase or a part’s name.
 * Everything is compared lowercased; plurality is handled at comparison time
 * rather than by stemming, which mangles words like "case".
 */
function searchTokens(text: string): string[] {
  const out: string[] = [];
  for (const word of text.toLowerCase().split(/[^a-z0-9]+/)) {
    if (word.length >= 2 && !K_COUNT_STOP_WORDS.has(word)) out.push(word);
  }
  return out;
}

/**
 * Substring containment is DIRECTIONAL, which is the trap here: "screws" does
 * not contain-match a part named "Screw 1", though "screw" matches both. Both
 * directions are tried so a plural in the question still finds a singular part.
 */
function tokenMatches(a: string, b: string): boolean {
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= 3 && long.includes(short);
}

/** Everything a member can be found by: its display name and its aliases. */
function memberText(snapshot: EntitySnapshot): string {
  return [displayNameOf(snapshot), ...snapshot.aliases.map((al) => al.value)].join(" ");
}

/**
 * Whether any ONE of these texts accounts for EVERY word the user used.
 *
 * ⚠ A PARTIAL match is not evidence, and this used to treat it as some
 * (2026-09-01, user-reported: "How many top cover screws are there" answered
 * `There are 2 top cover screws` from a set whose two members are "Bottom Cover"
 * and "Back cover"). The rule was tiered — 2 when one text carried ALL the
 * words, 1 when one carried ANY — and the best tier present won, so with
 * nothing better in the scene an overlap on the single most generic word in the
 * phrase won outright and a count of COVERS was reported as a count of SCREWS.
 * The word the question was actually about was matched by nothing at all.
 *
 * A coverage RATIO would not have fixed it either: qualifiers outnumber the head
 * noun, so a set of "Top Cover 1/2" covers two words of "top cover screws" and
 * clears any majority floor while still holding no screws. Nor is "the last word
 * must match" safe — it inverts on "screws on the top cover". Full coverage is
 * the only rule here that cannot be satisfied by the qualifiers alone.
 *
 * Coverage counts the QUERY's words, never the text's, so asking generally still
 * finds a specific set: "screws" is fully covered by "Top Cover Screw 1". It is
 * the other direction that must fail — extra words in the question that nothing
 * in the scene carries are the user saying WHICH screws, and the scene has no
 * answer for them.
 *
 * A near miss therefore falls through to the set NAME, then out of this tool, and
 * the skill's ranking takes it to the documents and then to the refusal string.
 * Same trade as the document relevance floors: a refusal is recoverable, a
 * confident wrong number is not. The cost is the opposite failure — a stray word
 * the parts do not carry ("small screws") now refuses where it used to answer.
 *
 * Per MEMBER, never pooled across the set: a set holding "Top Cover" and
 * "Screw 1" accounts between them for every word of "top cover screws" and
 * contains no such thing. The old tier-2 rule was per-text for this reason and
 * this keeps it.
 */
function coversEveryToken(
  tokens: ReadonlyArray<string>,
  texts: ReadonlyArray<string>,
): boolean {
  if (tokens.length === 0) return false;
  return texts.some((text) => {
    const words = searchTokens(text);
    return tokens.every((t) => words.some((w) => tokenMatches(t, w)));
  });
}

/** Everything a set's members can be found by, one entry per member. */
function memberTexts(
  set: SelectionSetData,
  byId: ReadonlyMap<string, EntitySnapshot>,
): string[] {
  const texts: string[] = [];
  for (const id of set.EntitySnapshotIDs ?? []) {
    const snapshot = byId.get(id);
    if (snapshot) texts.push(memberText(snapshot));
  }
  return texts;
}

/** A set the caller has to choose between, described by what is in it. */
function describeSet(
  set: SelectionSetData,
  byId: ReadonlyMap<string, EntitySnapshot>,
): { name: string; memberCount: number; sampleMembers: string[] } {
  const names: string[] = [];
  for (const id of set.EntitySnapshotIDs ?? []) {
    const snapshot = byId.get(id);
    if (snapshot) names.push(displayNameOf(snapshot));
  }
  return { name: set.Name, memberCount: names.length, sampleMembers: names.slice(0, 4) };
}

/** Question scaffolding that says nothing about which part is meant. */
const K_COUNT_STOP_WORDS = new Set([
  "how", "many", "much", "are", "is", "was", "were", "there", "the", "an",
  "of", "in", "on", "at", "to", "and", "or", "all", "total", "count", "number",
  "please", "me", "my", "we", "do", "does", "have", "has", "it", "its", "this",
  "that", "these", "those",
]);

/** Leaf parts under a node, or 0 when it has no children of its own. */
function countLeafDescendants(snapshot: EntitySnapshot): number {
  let total = 0;
  for (const child of snapshot.children) {
    const leaves = countLeafDescendants(child);
    total += leaves === 0 ? 1 : leaves;
  }
  return total;
}
